import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Button, TextField } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { BACKEND_URL } from '../utils/constants';
import { useUser } from '../context/UserContext';
import { extractApiError } from '../utils/apiError';

/**
 * Shared so the Settings upgrade path and the onboarding plan step run the same
 * SetupIntent flow. There must only ever be one implementation of this: the
 * ordering below (validate code, THEN confirm card) is load-bearing, and a
 * second copy would inevitably drift back to confirming the card first.
 */
export const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK as string);

type Props = {
  onSuccess: () => void;
  tier: string;
  /** Prefill from an already-attributed referral code; stays editable. */
  initialPromoCode?: string | null;
  submitLabel?: string;
};

export default function EmbeddedCheckoutForm({ onSuccess, tier, initialPromoCode, submitLabel }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const { getAccessTokenSilently } = useAuth0();
  const { refreshUser } = useUser();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string>("");
  const [promoCode, setPromoCode] = React.useState<string>(initialPromoCode || "");
  // A SetupIntent can only be confirmed once. If anything downstream fails we
  // keep the payment method it produced, so retrying doesn't re-confirm an
  // already-succeeded intent and strand the user on a dead form.
  const [confirmedPaymentMethod, setConfirmedPaymentMethod] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });

      // Check the referral code BEFORE taking the card. Validating afterwards
      // means a typo costs the user a confirmed SetupIntent they can't reuse.
      const code = promoCode.trim();
      if (code) {
        const check = await fetch(`${BACKEND_URL}/api/billing/validate-promo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({ promo_code: code })
        });
        const result = await check.json().catch(() => ({} as any));
        if (!check.ok || !result.valid) {
          setError(result.error || "That referral code isn't valid. Check the spelling, or leave it blank.");
          setSubmitting(false);
          return;
        }
      }

      let paymentMethod = confirmedPaymentMethod;
      if (!paymentMethod) {
        const { error, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
        if (error) {
          setError(error.message || 'Payment authorization failed.');
          setSubmitting(false);
          return;
        }
        const pm = setupIntent?.payment_method;
        paymentMethod = typeof pm === 'string' ? pm : pm?.id ?? null;
        setConfirmedPaymentMethod(paymentMethod);
      }

      const resp = await fetch(`${BACKEND_URL}/api/billing/start-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          payment_method: paymentMethod,
          tier: tier,
          promo_code: code || undefined
        })
      });
      if (!resp.ok) throw new Error(await extractApiError(resp, 'Failed to start subscription'));
      await refreshUser();
      onSuccess();
    } catch (e: any) {
      setError(e.message || 'Failed to start subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
      <PaymentElement />
      <TextField
        label="Referral code (optional)"
        value={promoCode}
        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
        size="small"
        fullWidth
        inputProps={{ maxLength: 32, style: { textTransform: 'uppercase' } }}
        helperText="Have a code from one of our team? Enter it here to apply your discount."
      />
      {error && <Alert severity="error">{error}</Alert>}
      <Button type="submit" variant="contained" disabled={submitting || !stripe || !elements}>
        {submitting ? 'Starting…' : (submitLabel || 'Confirm and start')}
      </Button>
    </form>
  );
}
